import React, { useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants } from "utils";
import { SOLO_ACCURACY_RULE } from "utils/soloAccuracy";
import { trackMeta } from "components/pages/Educator/Dashboard/AssignmentGroupCard";
import { ProficiencyLevelBadge } from "components/pages/Educator/Proficiency/ProficiencyLevelBadge";
import { BadgeXSmall } from "components/shared/Badges/BadgeXSmall";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { ReportModal } from "components/shared/ReportModal/ReportModal";
import {
  INCIDENTS_HEADING,
  IncidentsDisclaimer,
  INCIDENTS_TAB_LABEL,
  IncidentTagChip,
  IncidentCountsSummary,
  SafetyConcernsTag,
  TagChip,
} from "../shared/criticalIncidentsUi";
import { ResponsiveTabNav } from "../SessionSummaries/SessionReportTabs";
import { Container } from "../SessionSummaries/SessionSummary";
import { useLessonReportDetail } from "./useLessonReports";
import chatIcon from "assets/chat.svg";
import info2 from "assets/info2.svg";
import lessonPieIcon from "assets/icons/lesson-report/lesson-pie.svg";
import mathLevelIcon from "assets/icons/lesson-report/math-level-3.svg";
import warningIcon from "assets/icons/lesson-report/warning.svg";
import studentsIcon from "assets/icons/lesson-report/students.svg";
import chevronRightIcon from "assets/icons/lesson-report/chevron-right.svg";
import { trackClickedButton, trackViewedPage } from "utils/analytics";
// At-a-Glance is omitted from the card for now; its pieces stay commented out
// here (components, JSX, styles) in case the section returns. Its two rubric
// links come from the shared module, so they are kept imported for it.
// eslint-disable-next-line no-unused-vars
import { COLLAB_RUBRIC_URL, MATH_RUBRIC_URL } from "constants/rubricLinks";

const LESSON_CARD_TABS = [
  { key: "summary", label: "Summary" },
  { key: "incidents", label: INCIDENTS_TAB_LABEL },
];

/*
const MATH_BAR_COLORS = {
  1: { bar: colors.pink500, chip: colors.pink800 },
  2: { bar: "#EAB308", chip: colors.amber800 },
  3: { bar: colors.green500, chip: colors.green600 },
};
*/
const MATH_LEVEL_TEXT = { 1: colors.pink800, 2: colors.amber800, 3: colors.green700 };

const distributionSum = (distribution) =>
  (distribution || []).reduce((sum, level) => sum + (level.student_count || 0), 0);

const percentOf = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)}%` : "—");

// The level most students sit at. A tie goes to the lower level, so a split
// class reads as the one that needs more support. Null when nobody is scored.
export const mostCommonLevel = (distribution) =>
  (distribution || []).reduce((top, entry) => {
    const count = entry.student_count || 0;
    const topCount = top?.student_count || 0;
    const wins = count > topCount || (count > 0 && count === topCount && entry.level < top.level);
    return wins ? entry : top;
  }, null);

const collabLevel34Percent = (card) => {
  const total = distributionSum(card.collab_distribution);
  const top = (card.collab_distribution || [])
    .filter((l) => l.level >= 3)
    .reduce((sum, l) => sum + (l.student_count || 0), 0);
  return percentOf(top, total);
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        weekday: "short",
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      })
    : null;

const formatDateTime = (iso) =>
  iso
    ? `${formatDate(iso)} ${new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
    : "";

const lessonEyebrow = (lesson) =>
  [lesson?.course_name, lesson?.unit_ordinal_name, lesson?.ordinal_name]
    .filter(Boolean)
    .join(", ");

// Standard-flow bundles have no curriculum identity; they render their target
// standard, mirroring the assignment card's "CORE STANDARD - {code}" header.
const cardEyebrow = (card) =>
  card.lesson
    ? lessonEyebrow(card.lesson)
    : ["Core Standard", card.target_standard_code].filter(Boolean).join(" - ");

const cardTitle = (card) =>
  card.lesson?.lesson_name || card.target_standard_name || "Unknown lesson";

const studentsWord = (count) => (count === 1 ? "Student" : "Students");

const LESSON_REPORT_PAGE_NAME = "Lesson Report";

// Fields the payload does not carry are dropped by the analytics layer rather
// than sent as placeholders.
const cardAnalyticsContext = (card) => ({
  class: card.classroom_id,
  class_name: card.classroom_name,
  standard_id: card.target_standard_code,
  lesson_name: card.lesson?.lesson_name,
  session_count: card.session_count,
});

// A level's students. The per-student objects arrive with the server's
// cross-session change; until that is deployed the detail still carries the
// bare `student_names` list, which renders as names without a tooltip rather
// than as an empty level.
const studentsOf = (level) =>
  level.students ?? (level.student_names || []).map((name) => ({ name }));

// The Groups view scoped the way this report was computed: its class, over
// the same window (explicit bounds win over a preset). The filters a link
// lands on then say where its list came from, and widening any one of them
// walks back out.
const reportScopeParams = (card, filters) => {
  const params = new URLSearchParams({ tab: "groups" });
  if (card.classroom_id) params.set("classroom_id", card.classroom_id);
  if (filters?.startDate) {
    params.set("start_date", filters.startDate);
    if (filters.endDate) params.set("end_date", filters.endDate);
  } else {
    params.set("date_range", filters?.dateRange || "all_time");
  }
  return params;
};

// The flagged session, pinned, with the quote highlighted, and the student
// who said it as the Student filter. No standard: a bundle session may sit
// on a prerequisite, and the lesson's target would filter it out.
const transcriptDeepLink = (incident, card, filters) => {
  const params = reportScopeParams(card, filters);
  params.set("session_id", incident.session_id);
  if (incident.student_id) params.set("student_id", incident.student_id);
  if (incident.quote_timestamp) params.set("quote_ts", incident.quote_timestamp);
  if (incident.quote) params.set("quote", incident.quote);
  return `${constants.SESSION_HISTORY_PATH}?${params.toString()}`;
};

// Every session that fed the student's score: the student and the lesson's
// assignment (bundle). No standard, even from a math row: a bundle session
// can sit on a prerequisite standard, and a standard filter would hide it.
const sessionReportLink = (card, filters, student) => {
  const params = reportScopeParams(card, filters);
  params.set("student_id", student.student_id);
  params.set("assignment_id", card.assignment_id);
  return `${constants.SESSION_HISTORY_PATH}?${params.toString()}`;
};

const SeeReportLink = ({ href }) => (
  <TipReportLink href={href} target="_blank" rel="noopener noreferrer">
    See Session Report
    <img src={chevronRightIcon} alt="" aria-hidden="true" />
  </TipReportLink>
);

const LAST_QUESTIONS_WINDOW = 5;

// Math Scores tooltip: the student's pooled Solo Time accuracy for this standard.
// The accuracy is over the student's most recent questions; it only earns the
// "Last 5" label once they have that many.
const MathTip = ({ student, href }) => (
  <TipBody>
    <TipName>{student.name}</TipName>
    <TipSubheading title={SOLO_ACCURACY_RULE}>Solo Time Question Accuracy</TipSubheading>
    <TipRule>{SOLO_ACCURACY_RULE}</TipRule>
    <TipStats>
      <li>Total Questions: {student.total_questions ?? 0}</li>
      <li>Total Correct: {student.total_correct ?? 0}</li>
      {student.last_questions_accuracy != null && (
        <li>
          {(student.total_questions ?? 0) >= LAST_QUESTIONS_WINDOW
            ? "Last 5 Question Accuracy"
            : "Question Accuracy"}
          : {Math.round(student.last_questions_accuracy)}%
        </li>
      )}
    </TipStats>
    <SeeReportLink href={href} />
  </TipBody>
);

// Lesson Collaboration tooltip: the student's cross-session participation.
const CollabTip = ({ student, href }) => (
  <TipBody>
    <TipName>{student.name}</TipName>
    <TipStats>
      <li>Total Sessions: {student.total_sessions ?? 0}</li>
      <li>Total Dosage: {student.total_dosage_minutes ?? 0} min</li>
      {student.avg_collab_score != null && (
        <li>Avg Collaboration Score: {student.avg_collab_score}</li>
      )}
    </TipStats>
    <SeeReportLink href={href} />
  </TipBody>
);

// A level bucket's students, each name hovering its stats tooltip. `variant`
// picks the math or collaboration tooltip; `reportHref` builds the tooltip's
// session-report link for a student. The name list can be empty (a level
// nobody reached), which renders the placeholder.
const StudentList = ({
  keyPrefix,
  students,
  reportHref,
  variant = "collab",
  emptyLabel = "No Students",
}) => {
  const list = students || [];
  if (list.length === 0) return <EmptyStudents>{emptyLabel}</EmptyStudents>;
  const Tip = variant === "math" ? MathTip : CollabTip;
  const left = variant === "math";
  return list.map((student, i) => (
    <StudentRow key={student.student_id ?? `${student.name}-${i}`} $left={left}>
      {student.student_id ? (
        <Tooltip
          interactive
          placement="right"
          maxWidth={264}
          id={`${keyPrefix}-${student.student_id}`}
          tip={<Tip student={student} href={reportHref(student)} />}
        >
          <StudentName $left={left}>{student.name}</StudentName>
        </Tooltip>
      ) : (
        <StudentName $left={left}>{student.name}</StudentName>
      )}
    </StudentRow>
  ));
};

const MathLevelStat = ({ distribution }) => {
  const top = mostCommonLevel(distribution);
  return (
    <Stat>
      {top ? (
        <ProficiencyLevelBadge
          level={top.level}
          color={MATH_LEVEL_TEXT[top.level]}
          size={64}
          fontSize={32}
        />
      ) : (
        <StatIcon src={mathLevelIcon} alt="" aria-hidden="true" />
      )}
      <StatText>
        <StatNumber>{percentOf(top?.student_count, distributionSum(distribution))}</StatNumber>
        <StatLabel>
          {top ? `Math Level ${top.level}:` : "Math Level:"}
          <br />
          {top ? top.name : "No Scores Yet"}
        </StatLabel>
      </StatText>
    </Stat>
  );
};

const CardHeader = ({ card }) => (
  <HeaderRow>
    <HeaderLeft>
      <HeaderIcon src={lessonPieIcon} alt="" aria-hidden="true" />
      <HeaderTitleBlock>
        <Eyebrow>{cardEyebrow(card)}</Eyebrow>
        <LessonTitle>{cardTitle(card)}</LessonTitle>
        <HeaderMeta>
          {card.groups_with_session} of {card.groups_total} Groups with a Session
        </HeaderMeta>
        {card.last_session_completed_at && (
          <HeaderMeta>
            Last Session Completed: {formatDate(card.last_session_completed_at)}
          </HeaderMeta>
        )}
        {(card.incident_counts || []).length > 0 && (
          <SafetyConcernsTag />
        )}
      </HeaderTitleBlock>
    </HeaderLeft>
    <HeaderStats>
      <Stat>
        <StatIcon src={studentsIcon} alt="" aria-hidden="true" />
        <StatText>
          <StatNumber>{card.students_total}</StatNumber>
          <StatLabel>{studentsWord(card.students_total)}</StatLabel>
        </StatText>
      </Stat>
      <MathLevelStat distribution={card.math_distribution} />
      <Stat>
        <BadgeXSmall level={3} />
        <StatText>
          <StatNumber>{collabLevel34Percent(card)}</StatNumber>
          <StatLabel>
            Collaboration:
            <br />
            Level 3+
          </StatLabel>
        </StatText>
      </Stat>
    </HeaderStats>
  </HeaderRow>
);

/*
const MathDistributionBar = ({ distribution }) => (
  <DistributionBar>
    {(distribution || []).map((level) => {
      const paint = MATH_BAR_COLORS[level.level] || MATH_BAR_COLORS[1];
      return (
        <BarSegment key={level.level} $bg={paint.bar} $grow={level.student_count}>
          <BarChip $bg={paint.chip}>
            L{level.level}: {level.student_count} {studentsWord(level.student_count)}
          </BarChip>
        </BarSegment>
      );
    })}
  </DistributionBar>
);

const RubricLink = ({ href }) => (
  <RubricAnchor href={href} target="_blank" rel="noopener noreferrer">
    Rubric <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
  </RubricAnchor>
);
*/

const SummaryTab = ({ card, filters, detail, onViewIncidents, onReportIssue }) => {
  const reportHref = (student) => sessionReportLink(card, filters, student);
  return (
  <>
    {/* At-a-Glance, omitted for now.
    <SectionH1>At-a-Glance</SectionH1>
    <SubheadRow>
      <SectionH2>Math Score:</SectionH2>
      <RubricLink href={MATH_RUBRIC_URL} />
    </SubheadRow>
    <MathDistributionBar distribution={card.math_distribution} />
    <SubheadRow>
      <SectionH2>Collaboration:</SectionH2>
      <RubricLink href={COLLAB_RUBRIC_URL} />
    </SubheadRow>
    <CollabStatsRow>
      {(card.collab_distribution || []).map((level) => (
        <CollabStat key={level.level}>
          <BadgeXSmall level={level.level} />
          <div>
            <CollabStatCount>
              <StatNumber>{level.student_count}</StatNumber>
              <CollabStatUnit>{studentsWord(level.student_count)}</CollabStatUnit>
            </CollabStatCount>
            <StatLabel>
              Level {level.level}: {level.name}
            </StatLabel>
          </div>
        </CollabStat>
      ))}
    </CollabStatsRow>
    */}

    {(card.incident_counts || []).length > 0 && (
      <IncidentCountsSummary
        counts={card.incident_counts}
        onViewDetails={onViewIncidents}
      />
    )}

    <SectionH1>
      Math Scores
      <Tooltip
        id={`math-scores-table-tooltip-${card.assignment_id}`}
        tip={SOLO_ACCURACY_RULE}
      >
        <InlineInfoIcon src={info2} alt="Info" />
      </Tooltip>
    </SectionH1>
    <ScoresTable>
      <thead>
        <tr>
          <FocusHeaderCell>Focus</FocusHeaderCell>
          {[1, 2, 3].map((level) => (
            <LevelHeaderCell key={level}>
              <LevelHeaderContent>
                <ProficiencyLevelBadge level={level} color={MATH_LEVEL_TEXT[level]} size={48} />
                <span>{["Needs Support", "Needs Practice", "Ready to Lead"][level - 1]}</span>
              </LevelHeaderContent>
            </LevelHeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {(detail?.math_scores || []).map((row) => {
          const track = trackMeta(row.track_type);
          return (
            <tr key={`${row.track_type}-${row.standard_code}`}>
              <FocusCell>
                <FocusCellTop>
                  <TagChip $bg={track.tagBg} $border={track.tagBorder} $color={track.tagText}>
                    {track.label}
                  </TagChip>
                  <StandardCode>{row.standard_code}</StandardCode>
                </FocusCellTop>
                <div>
                  <StandardName>{row.standard_name || row.standard_code}</StandardName>
                </div>
              </FocusCell>
              {(row.levels || []).map((level) => (
                <NamesCell key={level.level} $empty={studentsOf(level).length === 0}>
                  <StudentList
                    variant="math"
                    keyPrefix={`math-${row.standard_code}-${level.level}`}
                    students={studentsOf(level)}
                    reportHref={reportHref}
                  />
                </NamesCell>
              ))}
            </tr>
          );
        })}
      </tbody>
    </ScoresTable>

    <SectionH1>Lesson Collaboration</SectionH1>
    <CollabGrid>
      {(detail?.collaboration || []).map((level) => (
        <CollabColumn key={level.level}>
          <CollabColumnHeader>
            <BadgeXSmall level={level.level} />
            <CollabHeaderText>
              <CollabCountRow>
                <StatNumber>{studentsOf(level).length}</StatNumber>
                <CollabCountUnit>{studentsWord(studentsOf(level).length)}</CollabCountUnit>
              </CollabCountRow>
              <CollabLevelName>{level.name}</CollabLevelName>
            </CollabHeaderText>
          </CollabColumnHeader>
          <CollabColumnBody $empty={studentsOf(level).length === 0}>
            <StudentList
              keyPrefix={`collab-${level.level}`}
              students={studentsOf(level)}
              reportHref={reportHref}
            />
          </CollabColumnBody>
        </CollabColumn>
      ))}
    </CollabGrid>
    <ReportIssueCta type="button" onClick={onReportIssue}>
      <img src={chatIcon} alt="" aria-hidden="true" />
      Report Issue to OKO
    </ReportIssueCta>
  </>
  );
};

const IncidentsTab = ({ card, filters, detail }) => {
  const incidents = detail?.incidents || [];
  return (
    <>
      <SectionH1>{INCIDENTS_HEADING}</SectionH1>
      <BodyText>
        <IncidentsDisclaimer />
      </BodyText>
      <InstanceCount>
        {incidents.length} {incidents.length === 1 ? "Instance" : "Instances"}
      </InstanceCount>
      <IncidentList>
        {incidents.map((incident, i) => (
          <IncidentItem key={i}>
            <IncidentLine>
              <IncidentStudent>{incident.student_name || "Unknown student"}</IncidentStudent>{" "}
              {formatDateTime(incident.session_date)}{" "}
              {(incident.tags || []).map((tag) => (
                <IncidentTagChip key={tag} tag={tag} />
              ))}
            </IncidentLine>
            <IncidentQuote
              href={transcriptDeepLink(incident, card, filters)}
              target="_blank"
              rel="noopener noreferrer"
            >
              &ldquo;{incident.quote}&rdquo;
            </IncidentQuote>
          </IncidentItem>
        ))}
      </IncidentList>
    </>
  );
};

export const LessonReportCard = ({ card, filters }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const { detail, error, loading } = useLessonReportDetail(
    card.assignment_id,
    filters,
    expanded,
  );

  const idPrefix = `lesson-report-${card.assignment_id}`;
  // Without safety concerns there is only the Summary, so no tab strip at all:
  // the body sits directly under the header.
  const hasIncidents = (card.incident_counts || []).length > 0;
  const effectiveTab = hasIncidents ? activeTab : "summary";
  // A refresh can take a mounted card's incidents away and later bring them
  // back; reset the stored tab so the strip never reappears on Safety Concerns
  // without the teacher choosing it.
  useEffect(() => {
    if (!hasIncidents) setActiveTab("summary");
  }, [hasIncidents]);

  return (
    <Accordion
      activeKey={expanded ? "report" : null}
      onSelect={(key) => {
        if (key != null && !expanded) {
          trackViewedPage(LESSON_REPORT_PAGE_NAME, cardAnalyticsContext(card));
        }
        setExpanded(key != null);
      }}
    >
      <Container eventKey="report">
        <Accordion.Header>
          <CardHeader card={card} />
        </Accordion.Header>
        {/* Mounted only while open: the badge SVGs carry fixed def ids, and a
            hidden duplicate in a collapsed card hijacks the visible ones. */}
        <Accordion.Body>
          {expanded && (
            <>
              {hasIncidents && (
                <ResponsiveTabNav
                  activeKey={effectiveTab}
                  onSelect={setActiveTab}
                  tabs={LESSON_CARD_TABS.map((tab) =>
                    tab.key === "incidents" ? { ...tab, icon: warningIcon } : tab,
                  )}
                  idPrefix={idPrefix}
                  ariaLabel="Lesson report sections"
                />
              )}
              <TabBody
                {...(hasIncidents && {
                  role: "tabpanel",
                  id: `${idPrefix}-tabpanel-${effectiveTab}`,
                  "aria-labelledby": `${idPrefix}-tab-${effectiveTab}`,
                })}
              >
                {loading && (
                  <CenteredSpinner>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  </CenteredSpinner>
                )}
                {!loading && error && <BodyText>{error}</BodyText>}
                {!loading && !error && effectiveTab === "summary" && (
                  <SummaryTab
                    card={card}
                    filters={filters}
                    detail={detail}
                    onViewIncidents={() => setActiveTab("incidents")}
                    onReportIssue={() => {
                      trackClickedButton("Report Issue", {
                        page_name: LESSON_REPORT_PAGE_NAME,
                        class: card.classroom_id,
                        lesson_name: card.lesson?.lesson_name,
                        assignment_id: card.assignment_id,
                      });
                      setReportModalOpen(true);
                    }}
                  />
                )}
                {!loading && !error && effectiveTab === "incidents" && (
                  <IncidentsTab card={card} filters={filters} detail={detail} />
                )}
              </TabBody>
            </>
          )}
        </Accordion.Body>
        <ReportModal
          show={reportModalOpen}
          onHide={() => setReportModalOpen(false)}
          reportLocation="Lesson Report"
          contextMessage={`[Lesson Report] ${cardTitle(card)} (assignment ${card.assignment_id})`}
        />
      </Container>
    </Accordion>
  );
};

const HeaderRow = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 0 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
`;

const HeaderIcon = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-family: "ABC Marfa", sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.orange750};
`;

const LessonTitle = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const HeaderMeta = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  flex-shrink: 0;
  margin-left: auto;
  /* The accordion's corners toggle sits absolutely at right: 24px, 32px wide
     (sessionHistory.css); 6rem keeps the stats 4rem clear of it. */
  margin-right: 6rem;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatIcon = styled.img`
  width: 64px;
  height: 64px;
`;

const StatText = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatNumber = styled.span`
  font-family: "ABC Marfa", sans-serif;
  font-weight: 600;
  font-size: 32px;
  line-height: 28px;
  color: ${colors.nightlyWoods500};
`;

const StatLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const TabBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fff;
`;

const CenteredSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const SectionH1 = styled.h3`
  font-family: "ABC Marfa", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${colors.nightlyWoods500};
  margin: 16px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const InlineInfoIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
  vertical-align: middle;
`;

const TipRule = styled.p`
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: #58605c;
  margin: 4px 0 8px;
`;

// const SectionH2 = styled.h4`
//   font-family: "ABC Marfa", sans-serif;
//   font-weight: 600;
//   font-size: 20px;
//   line-height: 28px;
//   color: ${colors.nightlyWoods500};
//   margin: 0;
// `;
// 
// const SubheadRow = styled.div`
//   display: flex;
//   /* Baseline, not center: SectionH1 carries its own top margin, which makes
//      centered links ride high against the heading text. */
//   align-items: baseline;
//   gap: 8px;
// `;
// 
// const RubricAnchor = styled.a`
//   display: inline-flex;
//   align-items: center;
//   gap: 8px;
//   font-family: Inter, sans-serif;
//   font-size: 18px;
//   line-height: 26px;
//   color: ${colors.blue700};
//   text-decoration: none;
//   padding: 0 8px;
// 
//   &:hover {
//     text-decoration: underline;
//     color: ${colors.blue700};
//   }
// `;
// 
// const DistributionBar = styled.div`
//   display: flex;
//   width: 100%;
//   border-radius: 8px;
//   overflow: hidden;
// `;
// 
// const BarSegment = styled.div`
//   flex-grow: ${(p) => p.$grow};
//   flex-basis: auto;
//   display: flex;
//   justify-content: center;
//   padding: 16px 4px;
//   background: ${(p) => p.$bg};
// `;
// 
// const BarChip = styled.span`
//   background: ${(p) => p.$bg};
//   border-radius: 4px;
//   padding: 2px 4px;
//   font-family: Inter, sans-serif;
//   font-weight: 700;
//   font-size: 14px;
//   line-height: 20px;
//   color: #fff;
//   white-space: nowrap;
// `;
// 
// const CollabStatsRow = styled.div`
//   display: flex;
//   gap: 24px;
// `;
// 
// const CollabStat = styled.div`
//   flex: 1;
//   display: flex;
//   align-items: center;
//   gap: 12px;
// `;
// 
// const CollabStatCount = styled.div`
//   display: flex;
//   align-items: baseline;
//   gap: 6px;
// `;
// 
// const CollabStatUnit = styled.span`
//   font-family: Inter, sans-serif;
//   font-size: 16px;
//   line-height: 18px;
//   color: ${colors.nightlyWoods500};
// `;
// 
// Fixed layout: an auto table widens a column to fit a nowrap name, so nothing
// ever truncates. Focus keeps its 268px; the level columns share the rest.
const ScoresTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    background: ${colors.gray50};
    border-bottom: 1px solid #858b87;
    padding: 16px;
    text-align: left;
    vertical-align: top;
  }
`;

const FocusHeaderCell = styled.th`
  width: 268px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
  vertical-align: middle !important;
`;

const LevelHeaderCell = styled.th`
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
  vertical-align: middle !important;
`;

const LevelHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FocusCell = styled.td`
  width: 268px;
`;

const FocusCellTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const StandardCode = styled.span`
  font-family: "ABC Marfa", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.nightlyWoods500};
`;

const StandardName = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  color: ${colors.nightlyWoods500};
`;

// An empty cell sits on a warmer panel than one with names; the doubled class
// outranks the table's own th/td background rule.
const NamesCell = styled.td`
  && {
    background: ${(p) => (p.$empty ? colors.milkGlass200 : colors.gray50)};
  }
`;

const StudentName = styled.div`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
  margin-bottom: 8px;
  text-align: ${(p) => (p.$left ? "left" : "center")};
  /* The design caps a name at 153px and truncates past it, whatever the
     column width; the tooltip carries the full name. min-width: 0 lets the
     flex item shrink below its content so the ellipsis can apply. */
  max-width: 153px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Each name centred on its own line: the shared Tooltip wraps its trigger in
// an inline-flex span that hugs the name, so the bubble opens right beside the
// text rather than at the cell edge, while a long name still truncates
// against the cell width.
const StudentRow = styled.div`
  display: flex;
  justify-content: ${(p) => (p.$left ? "flex-start" : "center")};
  min-width: 0;

  & > span {
    min-width: 0;
    max-width: 100%;
  }
`;

const EmptyStudents = styled.div`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  font-style: italic;
  text-align: center;
  color: ${colors.milkGlass950};
`;

// The tooltip content per the design: a 264px bubble (232px inside the shared
// bubble's 16px padding) with the name, the stats list, and a full-width
// secondary button.
const TipBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 232px;
  text-align: left;
  color: ${colors.nightlyWoods500};
`;

const TipName = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  margin-bottom: -12px;
`;

const TipSubheading = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: -12px;
`;

const TipStats = styled.ul`
  margin: 0;
  padding-left: 21px;
  list-style: disc outside;
  font-size: 14px;
  line-height: 20px;
`;

const TipReportLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 48px;
  padding: 0 8px 0 16px;
  border: 1px solid ${colors.blue500};
  border-radius: 8px;
  background: #fff;
  color: ${colors.blue500};
  font-weight: 700;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  text-decoration: none;

  &:hover {
    color: ${colors.blue700};
    border-color: ${colors.blue700};
  }

  img {
    width: 24px;
    height: 24px;
  }
`;

// minmax(0, 1fr): a plain 1fr track's minimum is its content, so a long name
// would widen its column instead of truncating.
const CollabGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
`;

const ReportIssueCta = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 24px;
  color: ${colors.nightlyWoods500};
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
  }
`;

const CollabColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Badge beside the count and level name, per the design.
const CollabColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px 0;
`;

const CollabHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${colors.nightlyWoods500};
`;

const CollabCountRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
`;

const CollabCountUnit = styled.span`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const CollabLevelName = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
`;

// An empty level sits on a warmer, slightly darker panel than one with names.
const CollabColumnBody = styled.div`
  background: ${(p) => (p.$empty ? colors.milkGlass200 : colors.gray50)};
  padding: 16px 24px;
  flex: 1;
`;

const BodyText = styled.p`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const InstanceCount = styled.div`
  font-family: Inter, sans-serif;
  font-size: 18px;
  line-height: 26px;
  color: ${colors.nightlyWoods500};
`;

const IncidentList = styled.ul`
  margin: 0;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IncidentItem = styled.li`
  font-family: Inter, sans-serif;
  font-size: 18px;
  line-height: 26px;
  color: ${colors.nightlyWoods500};
`;

const IncidentLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const IncidentStudent = styled.span`
  font-weight: 700;
`;

const IncidentQuote = styled.a`
  display: block;
  padding: 4px 24px 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: ${colors.blue700};
  text-decoration: underline;

  &:hover {
    color: ${colors.blue800};
  }
`;
